/**
 * Alignment Service — CCA-M1/M2 client
 *
 * Wraps /api/v1/alignment/* HTTP calls.
 */

import { mutate, fetchJson } from './base-roros.service';

export interface AlignmentConflict {
  layer: 'governance' | 'company-principles' | 'core-values' | 'mission-vision';
  principleId: string;
  principleTitle: string;
  reason: string;
}

export interface AlignmentCheckResult {
  status: 'ok' | 'skipped';
  conflicts: AlignmentConflict[];
}

export interface AlignmentCheckSubject {
  title: string;
  description?: string | null;
  rationale?: string | null;
}

export interface AlignmentSettings {
  /** alignment_flagging_enabled */
  enabled: boolean;
  /** ai_guidance_enabled (CCA-M2 AC-CCA-24, default true) */
  guidanceEnabled: boolean;
}

// ─── CCA-M2: Guidance bundle types ───────────────────────────────────────────

export type GuidanceLayer = 'GOVERNANCE' | 'COMPANY' | 'DIRECTION' | 'MARKET';

export interface GuidanceBundleEntry {
  layer: GuidanceLayer;
  id: string;
  title: string;
  description: string | null;
}

export type GuidanceBundle = GuidanceBundleEntry[];

export const GUIDANCE_LAYER_LABELS: Record<GuidanceLayer, string> = {
  GOVERNANCE:  'Governance',
  COMPANY:     'Company Principles & Values',
  DIRECTION:   'Direction & Strategy',
  MARKET:      'Market & Brand',
};

export const RorosAlignmentService = {
  check: (subject: AlignmentCheckSubject) =>
    mutate<AlignmentCheckResult>('/api/v1/alignment/check', 'POST', {
      subject,
    }),

  /** Acknowledge a conflict for a Decision (M1). */
  acknowledge: (decisionId: string, principleId: string) =>
    mutate('/api/v1/alignment/acknowledge', 'POST', { decisionId, principleId }),

  /** Acknowledge a conflict for a Goal (M3 AC-CCA-27). */
  acknowledgeGoal: (goalId: string, principleId: string) =>
    mutate('/api/v1/alignment/acknowledge', 'POST', {
      subjectId: goalId,
      subjectType: 'goal',
      principleId,
    }),

  /**
   * AC-CCA-28: Fetch prior acknowledged principleIds for an existing entity.
   * Returns empty array for entities with no prior acks.
   * 404 for foreign / other-org subjectId.
   */
  getAcknowledgements: (subjectId: string, subjectType: 'decision' | 'goal' | 'outcome') =>
    fetchJson<{ acknowledgedPrincipleIds: string[] }>(
      `/api/v1/alignment/acknowledgements?subjectId=${encodeURIComponent(subjectId)}&subjectType=${subjectType}`,
    ),

  getSettings: () =>
    fetchJson<AlignmentSettings>('/api/v1/alignment/settings'),

  setSettings: (opts: { enabled?: boolean; guidanceEnabled?: boolean }) =>
    mutate<AlignmentSettings>('/api/v1/alignment/settings', 'PUT', opts),

  /** CCA-M2 AC-CCA-23: Fetch the broad GuidanceBundle for the panel */
  getContext: () =>
    fetchJson<{ bundle: GuidanceBundle }>('/api/v1/alignment/context'),
};
